import React from 'react';
import PropTypes from 'prop-types';
import { TableContainer, Table as TableM, Box } from '@material-ui/core';
import { withApi, withNotification } from 'wrappers';
import { isFunction } from 'utils/functions';
import { DEFAULT_MUI_DATATABLE_SIZE } from 'constantes/constants';
import Head from './Head';
import Body from './Body';
import BodySelect from './select/Body';
import BodyExpandable from './expandable/Body';
import Pagination from './Pagination';
import ToolBar from './ToolBar';

/* For refresh the table with Serverside, you will can do this steps
* ------ En el componente padre -------
* -> Crear la referencia
*    this.child = React.createRef();
* -> Enviar referencia por props
*    <TableGeneral forwardedRef={this.child} />
* -> Para ejecutar el metodo refrescar
*    this.child.current.refresh();
*/
class Table extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      isLoading: true,
      page: 0,
      rowsPerPage: props.rowsPerPage,
      order: '',
      orderBy: '',
      total: 0,
      data: [],
      filters: {},
      checkedAll: false,
    };
    const { forwardedRef } = this.props;
    if (forwardedRef) {
      forwardedRef.current = { refresh: this.serverSide };
    }
  }

  componentDidMount() {
    const { serverSideUrl } = this.props;
    if (serverSideUrl) {
      this.serverSide();
    } else {
      this.processData();
      this.setState({ isLoading: false });
    }
  }

  // Funcion para evaluar cambios en los datos
  componentDidUpdate(prevProps) {
    const { data, serverSideUrl } = this.props;
    if (prevProps.data !== data && !serverSideUrl) {
      this.processData();
    }
  }

  // Funcion para cambiar los filtros
  setFilter = (filters) => {
    if (this.props.serverSideUrl) {
      this.setState({ filters, isLoading: true, page: 0 }, this.serverSide);
    } else {
      const data = this.filterData(filters);
      this.setState({ data, page: 1 });
    }
  };

  // Funcion para cambiar de pagina
  setPage = (page) => {
    if (this.props.serverSideUrl) {
      this.setState({ page, isLoading: true }, this.serverSide);
    } else {
      this.setState({ page });
    }
  };

  // Funcion para cambiar los registros por pagina
  setRowsPerPage = (rowsPerPage) => {
    if (this.props.serverSideUrl) {
      this.setState({ rowsPerPage, page: 0, isLoading: true }, this.serverSide);
    } else {
      this.setState({ rowsPerPage, page: 0 });
    }
  };

  // Funcion para forma de ordenar, ascendente o descendente, segun seleccionada
  setOrderBy = (orderBy) => {
    const isAsc = orderBy === this.state.orderBy && this.state.order === 'asc';
    const order = isAsc ? 'desc' : 'asc';

    if (this.props.serverSideUrl) {
      this.setState({ order, orderBy, isLoading: true }, this.serverSide);
    } else {
      const data = this.orderData(order, orderBy);
      this.setState({ order, orderBy, data });
    }
  };

  // Funcion para filtrar los elementos segun parametros de busqueda sin serverside
  filterData = (filters) => {
    const { data } = this.props;
    const filter = Object.values(filters);
    let dataForm = [];
    for (let i = 0; i < data.length; i++) {
      const arrayData = [];
      arrayData.push(
        Object.values(data[i]),
      );
      if (arrayData[0].indexOf(filter[0]) !== -1) {
        dataForm = [data[i]];
        break;
      } else {
        dataForm = data;
      }
    }
    return dataForm;
  };

  // Funcion que me devuelve registros organizados segun parametros recibidos
  orderData = (order, orderBy) => {
    const { data } = this.props;
    const ascending = order === 'asc' ? 1 : -1;
    const orderedData = data.sort((a, b) => {
      if (a[orderBy] < b[orderBy]) {
        return -1 * ascending;
      }
      if (a[orderBy] > b[orderBy]) {
        return 1 * ascending;
      }
      return 0;
    });

    return orderedData;
  };

  // Función que filtra solo datos segun pagina y registros por pagina
  pageData = (data) => {
    if (this.props.serverSideUrl) {
      return data;
    }

    const { page, rowsPerPage } = this.state;
    const initialRow = page * rowsPerPage;
    return data.slice(initialRow, initialRow + rowsPerPage);
  };

  // Funcion encargada de cargar los datos solicitados segun parametros
  serverSide = async () => {
    const {
      serverSideUrl, serverSideData, doGet, onDataLoaded,
    } = this.props;
    const {
      page, rowsPerPage, order, orderBy, filters,
    } = this.state;
    const sorts = {};
    if (orderBy && orderBy !== '') {
      sorts[orderBy] = order;
    }

    const params = {
      url: serverSideUrl,
      data: {
        perPage: rowsPerPage,
        page: page + 1,
        sorts: JSON.stringify(sorts),
        filters: JSON.stringify(filters),
        ...serverSideData,
      },
    };

    try {
      const resp = await doGet(params);
      const { data, total } = resp;
      this.setState({
        data,
        total: total || 0,
        isLoading: false,
      });
      if (isFunction(onDataLoaded)) {
        onDataLoaded(resp);
      }
    } catch (error) {
      this.props.appError('Ocurrio un error cargando los datos de la tabla', error);
    }
  };

  // Funcion que llama las correspondientes funciones segun el caso
  processData = () => {
    const { order, orderBy } = this.state;
    const orderData = this.orderData(order, orderBy);
    this.setState({ data: orderData, total: orderData.length });
  };

  onCheckedAll = (data) => {
    this.setState((prevState) => ({
      ...prevState,
      checkedAll: data,
    }));
  };

  render() {
    const {
      columns, title, onCreate, createPermissions, onRowsSelect, expandable, 
      ExtraToolBar, date, onDownloadExcel, onDownloadPdf, updatePermissions, 
      filtersTable, titleHelpMessage, selectableCondition,
    } = this.props;
    const {
      page, rowsPerPage, order, orderBy, data, total, filters, isLoading, checkedAll,
    } = this.state;
    return (
      <Box>
        <ToolBar
          dates={date}
          title={title}
          columns={columns}
          filters={filters}
          setFilter={this.setFilter}
          onCreate={onCreate}
          onDownloadPdf={onDownloadPdf}
          filtersTable={filtersTable}
          onDownloadExcel={onDownloadExcel}
          titleHelpMessage={titleHelpMessage}
          createPermissions={createPermissions}
          updatePermissions={updatePermissions}
          ExtraToolBar={ExtraToolBar}
        />
        <TableContainer>
          <TableM size={DEFAULT_MUI_DATATABLE_SIZE}>
            <Head
              columns={columns}
              order={order}
              data={this.pageData(data)}
              orderBy={orderBy}
              setOrderBy={this.setOrderBy}
              extraColumn={Boolean(expandable)}
              onRowsSelect={onRowsSelect}
              checkedAll={checkedAll}
              onCheckedAll={this.onCheckedAll}
              selectableCondition={selectableCondition}
            />
            {Boolean(onRowsSelect) && (
              <BodySelect
                columns={columns}
                data={this.pageData(data)}
                isLoading={isLoading}
                checkedAll={checkedAll}
                onRowsSelect={onRowsSelect}
                selectableCondition={selectableCondition}
              />
            )}
            {Boolean(expandable) && (
              <BodyExpandable
                columns={columns}
                data={this.pageData(data)}
                isLoading={isLoading}
                expandable={expandable}
              />
            )}
            {!expandable && !onRowsSelect && (
              <Body columns={columns} data={this.pageData(data)} isLoading={isLoading} />
            )}
          </TableM>
        </TableContainer>
        <Pagination
          page={page}
          rowsPerPage={rowsPerPage}
          total={total}
          setPage={this.setPage}
          setRowsPerPage={this.setRowsPerPage}
        />
      </Box>
    );
  }
}

Table.defaultProps = {
  data: [],
  date: false,
  rowsPerPage: 100,
  filtersTable: true,
  selectableCondition: () => true,
};

Table.propTypes = {
  // Array de columnas, cada columna puede tener varias llaves
  // label: Es el texto que se quiere mostrar en el Toobar de la tabla
  // name: Texto que identificara datos a poner en cada columna y registros a buscar
  // align: alineacion de las columnas left, rigth, center, por defecto es left
  // component: React component o función, en caso de ser funcion pasa como parametro el registro
  // filter: booleano para filtrar registros, por defecto true
  // width: tamaño de la columna
  // type: tipo de columna, por el momento se acepta 'number'
  columns: PropTypes.oneOfType([PropTypes.array]).isRequired,
  // Array de objetos para mostrar en en la tabla
  data: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.object])),
  // Titulo de la tabla
  title: PropTypes.string,
  // Mensaje de recomendaciones
  titleHelpMessage: PropTypes.string,
  // Url a la cual se buscaran los datos
  serverSideUrl: PropTypes.string,
  // Data para la url en caso de necesitar enviar algun parametro extra
  serverSideData: PropTypes.oneOfType([PropTypes.object]),
  // Funcion para crear nuevos registros
  onCreate: PropTypes.func,
  // Funcion para descargar un excel
  onDownloadExcel: PropTypes.func,
  // Funcion para descargar un pdf
  onDownloadPdf: PropTypes.func,
  // Funcion para retornar la data que llega del servidor
  onDataLoaded: PropTypes.func,
  // Funcion para expandir la columna, pasa como parametro el registro
  expandable: PropTypes.func,
  // Funcion que devuelve las columnas seleccionadas
  onRowsSelect: PropTypes.func,
  // Elemento para crear nuevos botones que se deseen adicionar en la parte superior de la tabla.
  ExtraToolBar: PropTypes.node,
  // filter: booleano para filtrar registros, por defecto true
  filtersTable: PropTypes.bool,
  // Permisos para el boton de crear
  createPermissions: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
  // Permisos para el boton de editar
  updatePermissions: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
  doGet: PropTypes.func,
  appError: PropTypes.func.isRequired,
  forwardedRef: PropTypes.oneOfType([PropTypes.object]),
  // Muestra las fechas en los filtros
  date: PropTypes.bool,
  rowsPerPage: PropTypes.number,
  // Elemento para habilitar o deshabilitar el onRowSelect
  selectableCondition: PropTypes.func,
};

export default withApi(withNotification(Table));
